import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getUserData as getUserDataFromFirebase, type UserData } from '@/lib/firebase/user';

export function useUserSync() {
  const { user } = useAuth();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initial fetch of user data
  useEffect(() => {
    async function fetchUserData() {
      if (!user?.email) {
        setUserData(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getUserDataFromFirebase(user.email);
        setUserData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    }

    fetchUserData();
  }, [user?.email]);

  // Periodic sync (every 30 seconds)
  useEffect(() => {
    if (!user) return;

    const syncInterval = setInterval(async () => {
      try {
        const freshData = await getUserDataFromFirebase(user?.uid || '');
        setUserData(prev => {
          // Only update if there are actual changes
          if (JSON.stringify(prev) !== JSON.stringify(freshData)) {
            return freshData;
          }
          return prev;
        });
      } catch (err) {
        console.error('Error syncing user data:', err);
      }
    }, 30000); // 30 seconds

    return () => clearInterval(syncInterval);
  }, [user]);

  // Function to force refresh user data
  const refreshUserData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const freshData = await getUserDataFromFirebase(user?.uid || '');
      setUserData(freshData);
      setError(null);
    } catch (err) {
      console.error('Error refreshing user data:', err);
      setError('Failed to refresh user data');
    } finally {
      setLoading(false);
    }
  };

  return {
    userData,
    loading,
    error,
    refreshUserData,
    setUserData
  };
} 